import { styled } from "@mui/system";
import COLORS from "../../../theme/Colors";
import FONTS_FAMILY from "../../../theme/FontsFamily";

// Layout wrapper components
export const PageWrapper = styled("div")({
    display: "flex",
    width: "100%",
    minHeight: "100vh",
    backgroundColor: "#F9F9FF",
    boxSizing: "border-box",
    "html.dark &": {
        backgroundColor: "#000000",
    },
});

export const MainPanel = styled("div")({
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minWidth: 0,
    boxSizing: "border-box",
});

export const ContentWrapper = styled("div")({
    padding: "40px", // Tailwind margin-desktop
    display: "flex",
    flexDirection: "column",
    flex: 1,
    overflowY: "auto",
    boxSizing: "border-box",
    maxWidth: "1280px",
    width: "100%",
    margin: "0 auto",
    "@media (max-width: 1024px)": {
        padding: "24px 20px",
    },
    "@media (max-width: 768px)": {
        padding: "16px 14px !important",
    },
});

// Welcome section typography
export const WelcomeHeader = styled("h2")({
    fontFamily: FONTS_FAMILY.bold.title,
    fontSize: "32px", // Tailwind headline-md
    color: "#192873", // Tailwind primary
    margin: "0 0 8px 0",
    fontWeight: "600",
    textAlign: "left",
    lineHeight: "40px",
    "@media (max-width: 768px)": {
        fontSize: "20px",
        lineHeight: "26px",
        margin: "0 0 12px 0",
    },
    "html.dark &": {
        color: "#ffffff",
    },
});

export const WelcomeSubtitle = styled("p")({
    fontFamily: FONTS_FAMILY.regular.body,
    fontSize: "16px", // Tailwind body-md
    color: "#46464F", // Tailwind on-surface-variant
    margin: "0 0 32px 0",
    textAlign: "left",
    lineHeight: "24px",
    maxWidth: "42rem",
    "@media (max-width: 768px)": {
        display: "none",
    },
    "html.dark &": {
        color: "#cbd5e1",
    },
});

// Metrics Cards components
export const MetricsGrid = styled("div")({
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: "24px", // Tailwind gap-6
    width: "100%",
    boxSizing: "border-box",
});

export const MetricCard = styled("div")({
    backgroundColor: "#FFFFFF",
    borderRadius: "12px",
    padding: "20px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    position: "relative",
    overflow: "hidden",
    border: "1px solid #E2E8F0",
    boxShadow: "none",
    transition: "all 0.2s ease",
    "&:hover": {
        transform: "translateY(-2px)",
        boxShadow: "none",
        borderColor: "#CBD5E1",
    },
    "html.dark &": {
        backgroundColor: "#0a0a0a",
        borderColor: "#222222",
    },
    "html.dark &:hover": {
        borderColor: "#333333",
    },
});

export const MetricAccentBar = styled("div")(({ color }) => ({
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "4px",
    backgroundColor: color || "#1A234E",
}));

export const MetricLabel = styled("span")({
    fontFamily: FONTS_FAMILY.bold.body,
    fontSize: "13px",
    color: "#64748B",
    fontWeight: "700",
    marginBottom: "12px",
    "html.dark &": {
        color: "#94a3b8",
    },
});

export const MetricBottomRow = styled("div")({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
});

export const MetricNumber = styled("span")({
    fontFamily: FONTS_FAMILY.bold.title,
    fontSize: "32px",
    fontWeight: "700",
    color: "#0F2350",
    lineHeight: 1,
    "html.dark &": {
        color: "#ffffff",
    },
});

export const MetricIconWrapper = styled("div")(({ color }) => ({
    width: "44px",
    height: "44px",
    borderRadius: "10px",
    backgroundColor: color ? `${color}15` : "rgba(26, 35, 78, 0.08)",
    color: color || "#1A234E",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "& svg": {
        fontSize: "24px",
    },
    "html.dark &": {
        backgroundColor: color ? `${color}30` : "rgba(255, 255, 255, 0.1)",
    },
}));

// Legacy Pill Menu Bar
export const PillMenuContainer = styled("div")({
    display: "flex",
    gap: "12px",
    margin: "32px 0 24px 0",
});

export const PillButton = styled("button")(({ active }) => ({
    padding: "8px 20px",
    borderRadius: "20px",
    border: "none",
    fontFamily: FONTS_FAMILY.bold.body,
    fontSize: "14px",
    fontWeight: "700",
    cursor: "pointer",
    transition: "all 0.2s ease",
    backgroundColor: active ? "#192873" : "#EBF0FF",
    color: active ? "#FFFFFF" : "#192873",
    "&:hover": {
        backgroundColor: active ? "#192873" : "#DEE8FF",
        color: active ? "#FFFFFF" : "#192873",
    },
    "html.dark &": {
        backgroundColor: active ? "#2563eb" : "#111111",
        color: active ? "#FFFFFF" : "#cbd5e1",
    },
    "html.dark &:hover": {
        backgroundColor: active ? "#2563eb" : "#1a1a1a",
        color: "#FFFFFF",
    },
}));

// Main Content Box (contains tabs, filters, list)
export const MainDataBox = styled("div")({
    backgroundColor: "#FFFFFF",
    border: "1px solid #E7EEFF",
    borderRadius: "12px",
    boxShadow: "0 2px 12px rgba(15, 35, 80, 0.03)",
    display: "flex",
    flexDirection: "column",
    minHeight: "500px",
    height: "fit-content",
    width: "100%",
    boxSizing: "border-box",
    overflow: "visible",
    "@media (max-width: 768px)": {
        padding: "0px !important",
        borderRadius: "12px",
        boxShadow: "none",
        border: "none",
        backgroundColor: "transparent",
    },
    "html.dark &": {
        backgroundColor: "#0a0a0a",
        borderColor: "#222222",
    },
});

// Legacy / shared components compatibility
export const Container = styled("div")({
    display: "flex",
    flexDirection: "column",
    width: "100%",
});

export const SectionTitle = styled("h1")({
    margin: 0,
    color: "#192873",
    fontFamily: FONTS_FAMILY.regular.title,
    textAlign: "left",
    fontSize: "18px",
    "html.dark &": {
        color: "#ffffff",
    },
});

export const DocumentsContainer = styled("div")({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "24px",
});

export const ListContainer = styled("div")({
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    width: "100%",
    padding: "0 32px 32px 32px",
    boxSizing: "border-box",
    "@media (max-width: 768px)": {
        padding: "0 !important",
    },
});

export const SectionTextDivider = styled("div")({
    width: "5%",
});

export const SectionTitleFilter = styled("h1")({
    marginBottom: "0px",
    color: "#192873",
    fontFamily: FONTS_FAMILY.regular.title,
    textAlign: "left",
    fontSize: "15px",
    "html.dark &": {
        color: "#ffffff",
    },
});

export const RequestsWrapper = styled("div")({
    backgroundColor: COLORS.white,
    borderRadius: "12px",
    width: "100%",
    boxSizing: "border-box",
    "html.dark &": {
        backgroundColor: "#000000 !important",
    }
});

